/**
 * LeadDetailPanel - Full Lead Profile Panel
 * 
 * Extracted from FeedHome.js for use across all views.
 * Adapts to people, company, job, or news results.
 * 
 * SINGLE SOURCE OF TRUTH for lead detail rendering.
 * Used by: FeedHome, FeedWorkspace, and any other feed view.
 */

import React, { useState, useEffect } from 'react';
import {
  Briefcase,
  Building2,
  MapPin,
  Mail,
  Phone,
  Linkedin,
  ExternalLink,
  Users,
  Target,
  TrendingUp,
  Check,
  X,
  Loader2,
  Send,
  Copy,
  Sparkles,
  ChevronDown,
  ArrowLeft,
  MousePointer,
  Pencil,
  PlusSquare,
  MinusCircle,
  CheckCircle,
  Zap,
  Lightbulb,
  RefreshCw,
} from 'lucide-react';
import api from '../../utils/axios';
import { useToast } from './Toast';

// =============================================================================
// COLLAPSIBLE SECTION - Progressive disclosure for secondary information
// =============================================================================
interface CollapsibleSectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  badge?: string | number;
  priority?: 'primary' | 'secondary' | 'tertiary';
}

function CollapsibleSection({ 
  title, 
  children, 
  defaultOpen = false, 
  badge,
  priority = 'secondary' 
}: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  // Primary sections are always open, secondary/tertiary start collapsed
  const effectiveOpen = priority === 'primary' ? true : isOpen;
  const canToggle = priority !== 'primary';
  
  return (
    <div className="border-b border-black/5">
      <button
        onClick={() => canToggle && setIsOpen(!isOpen)}
        className={`w-full px-5 py-3 flex items-center justify-between ${
          canToggle ? 'hover:bg-black/[0.02] cursor-pointer' : 'cursor-default'
        } transition-colors`}
        disabled={!canToggle}
      >
        <div className="flex items-center gap-2">
          <p className="text-xs font-semibold text-black/40 uppercase tracking-wide">{title}</p>
          {badge !== undefined && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-[var(--eq-purple)]/10 text-[var(--eq-purple-text)] rounded-full">
              {badge}
            </span>
          )}
        </div>
        {canToggle && (
          <ChevronDown 
            className={`w-4 h-4 text-black/30 transition-transform duration-200 ${
              effectiveOpen ? 'rotate-180' : ''
            }`} 
          />
        )}
      </button>
      <div 
        className={`overflow-hidden transition-all duration-200 ease-out ${
          effectiveOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-5 pb-4">
          {children}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// DATA QUALITY INDICATOR - Shows completeness of lead data
// =============================================================================
interface DataQualityProps {
  hasEmail: boolean;
  hasPhone: boolean;
  hasLinkedIn: boolean;
  hasCompany: boolean;
  hasLocation: boolean;
}

function DataQualityIndicator({ hasEmail, hasPhone, hasLinkedIn, hasCompany, hasLocation }: DataQualityProps) {
  const score = [hasEmail, hasPhone, hasLinkedIn, hasCompany, hasLocation].filter(Boolean).length;
  const percentage = Math.round((score / 5) * 100);
  
  const getColor = () => {
    if (percentage >= 80) return 'bg-emerald-500';
    if (percentage >= 60) return 'bg-[var(--eq-teal)]';
    if (percentage >= 40) return 'bg-amber-500';
    return 'bg-slate-300';
  };
  
  const getLabel = () => {
    if (percentage >= 80) return 'Complete';
    if (percentage >= 60) return 'Good';
    if (percentage >= 40) return 'Partial';
    return 'Limited';
  };
  
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-black/5 rounded-full overflow-hidden">
        <div 
          className={`h-full ${getColor()} transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="text-[10px] font-medium text-black/40 whitespace-nowrap">
        {getLabel()} ({percentage}%)
      </span>
    </div>
  );
}

// =============================================================================
// DATE FORMATTING HELPER - Convert ISO dates to human-readable format
// =============================================================================
const formatDate = (dateStr: string): string => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr; // Return as-is if invalid
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
  } catch {
    return dateStr;
  }
};

const parseRawData = (value: any): Record<string, any> | null => {
  if (!value) return null;
  if (typeof value === 'object') return value as Record<string, any>;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return null;
};

// =============================================================================
// COMPANY LOGO WITH FALLBACK - Real logos with graceful fallback
// =============================================================================
function CompanyLogoWithFallback({ 
  logoUrl, 
  companyName, 
  size = 'md' 
}: { 
  logoUrl: string; 
  companyName: string; 
  size?: 'sm' | 'md' | 'lg';
}) {
  const [failed, setFailed] = useState(false);
  
  const sizeClasses = {
    sm: 'w-10 h-10',
    md: 'w-12 h-12',
    lg: 'w-14 h-14',
  };
  
  return (
    <div className={`${sizeClasses[size]} rounded-lg overflow-hidden bg-white flex-shrink-0 border border-slate-200 shadow-sm flex items-center justify-center`}>
      {!failed ? (
        <img 
          src={logoUrl}
          alt={companyName}
          className="w-full h-full object-contain p-1"
          onError={() => setFailed(true)}
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-[var(--eq-purple)] to-[var(--eq-teal)] flex items-center justify-center text-white text-sm font-bold">
          {companyName.charAt(0).toUpperCase()}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// TYPES
// =============================================================================
export interface LeadDetailPanelProps {
  lead: any;
  onClose: () => void;
  onSimilarSearch?: (query: string, type: string) => void;
  customFields?: any[];
  fieldOrder?: string[];
  status?: string;
  onStatusChange?: (status: string) => void;
  searchMode?: 'people' | 'company' | 'jobs' | 'demand';
  isMobile?: boolean;
}

// =============================================================================
// LEAD DETAIL PANEL COMPONENT
// =============================================================================
export function LeadDetailPanel({ 
  lead, 
  onClose, 
  onSimilarSearch, 
  customFields = [], 
  fieldOrder = [], 
  status = 'inbox', 
  onStatusChange, 
  searchMode = 'people',
  isMobile = false 
}: LeadDetailPanelProps) {
  const toast = useToast();
  const [copied, setCopied] = useState<string | null>(null);
  const [showCompanyMenu, setShowCompanyMenu] = useState(false);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [foundContacts, setFoundContacts] = useState<any[]>([]);
  const [showContactsSection, setShowContactsSection] = useState(false);
  
  const [emailConnection, setEmailConnection] = useState<{ connected: boolean; provider?: string; email?: string } | null>(null);
  
  // On-demand ZoomInfo contact enrichment for Precision Search results
  const [isEnrichingContact, setIsEnrichingContact] = useState(false);
  const [enrichedContactData, setEnrichedContactData] = useState<any>(null);
  const [enrichmentAttempted, setEnrichmentAttempted] = useState(false);
  
  const [whyNowData, setWhyNowData] = useState<any>(null);
  const [isLoadingWhyNow, setIsLoadingWhyNow] = useState(false);
  
  // Extract lead data from various possible structures
  const rawData = parseRawData(lead.raw_data || lead.rawData);
  const data =
    lead.data ||
    lead.lead_data ||
    (rawData ? { ...rawData, ...lead } : lead);
  // Detect result type
  const leadType = lead.type || lead.lead_type;
  const isCompanyResult = leadType === 'company' || searchMode === 'company' || !!data.companySize || !!data.revenue;
  const isJobResult = leadType === 'job' || leadType === 'jobs' || searchMode === 'jobs' || !!data.postedDate || !!data.sourceUrl;
  const isNewsResult = leadType === 'news' || !!data.publishedDate || !!data.articleUrl;
  
  const firstName = data.firstName || data.first_name || lead.first_name || '';
  const lastName = data.lastName || data.last_name || lead.last_name || '';
  const fullName = data.name || data.fullName || `${firstName} ${lastName}`.trim() || 'Unknown';
  const email = data.email || lead.email || lead.lead_email || '';
  const phone = data.phone || data.directPhoneNumber || lead.phone || '';
  const mobilePhone = data.mobilePhone || data.mobile_phone || '';
  const jobTitle = data.jobTitle || data.title || data.job_title || lead.job_title || '';
  const companyName = data.companyName || data.company_name || lead.company_name || '';
  const linkedinUrl = data.linkedinUrl || data.linkedin_url || lead.linkedin_url || '';
  const location = data.city ? `${data.city}${data.state ? `, ${data.state}` : ''}${data.country ? `, ${data.country}` : ''}` : (data.location || '');
  const industry = data.industry || data.primaryIndustry || lead.industry || '';
  const companyRevenue = data.companyRevenue || data.company_revenue || data.revenue || '';
  const companyEmployees = data.companyEmployees || data.employee_count || data.employeeCount || '';
  const summary = data.summary || data.bio || data.description || data.shortDescription || '';
  const yearFounded = data.yearFounded || data.year_founded || '';
  const companyWebsite = data.website || data.companyWebsite || '';
  const linkedInUrl = data.linkedinUrl || data.linkedInUrl || data.linkedin_url || lead.linkedin_url || '';
  const twitterUrl = data.twitterUrl || data.twitter_url || '';
  const ticker = data.ticker || '';
  const fundingAmount = data.fundingAmount || data.funding_amount || '';
  const latestFundingRound = data.latestFundingRound || data.latest_funding_round || '';
  
  // Professional details for pills
  const managementLevel = data.managementLevel || data.management_level || '';
  const experienceYears = data.yearsExperience || data.years_experience || data.experienceYears || '';
  const education = data.education || [];
  const skills = data.skills || [];
  const employmentHistory = data.employmentHistory || data.employment_history || data.workExperience || data.work_experience || data.experience || [];
  
  // ContactOut precision search indicators
  const isPrecisionMatch = data._precisionMatch || data.precisionMatch || false;
  const matchedKeywords = data._matchedKeywords || data.matchedKeywords || [];
  const workStatus = data.workStatus || data.work_status || '';
  const isOpenToWork = workStatus === 'open_to_work' || data.openToWork || data.isOpenToWork;
  
  // Profile picture and company logo
  const profilePicture = data.profilePicture || data.profilePictureUrl || data.imageUrl || 
    data.personImageUrl || data.photo || data.avatar || null;
  
  // Company website for logo lookup
  const website = data.website || data.companyWebsite || data.domain || '';
  
  // Extract domain for logo API fallback
  const extractDomain = (url: string): string => {
    if (!url) return '';
    try {
      const urlWithProtocol = url.startsWith('http') ? url : `https://${url}`;
      const parsed = new URL(urlWithProtocol);
      return parsed.hostname.replace('www.', '');
    } catch {
      return url.replace(/^(https?:\/\/)?(www\.)?/, '').split('/')[0];
    }
  };
  
  // Generate company name-based domain guess
  const guessCompanyDomain = (name: string): string => {
    if (!name) return '';
    const lowerName = name.toLowerCase().trim();
    return `${lowerName.replace(/[^a-z0-9]/g, '')}.com`;
  };
  
  // Get the best available company logo URL - using Google's favicon service
  const getCompanyLogoUrl = (): string | null => {
    const explicitLogo = data.companyLogo || data.companyLogoUrl || data.logoUrl || 
      data.logo || data.company_logo;
    if (explicitLogo) return explicitLogo;
    
    const domain = extractDomain(website);
    if (domain) return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
    
    if (companyName) {
      const guessedDomain = guessCompanyDomain(companyName);
      if (guessedDomain) return `https://www.google.com/s2/favicons?domain=${guessedDomain}&sz=128`;
    }
    
    return null;
  };
  
  const companyLogo = getCompanyLogoUrl();

  // =============================================================================
  // INLINE FIELD EDITING
  // =============================================================================
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [userEdits, setUserEdits] = useState<Record<string, any>>({});
  const [customUserFields, setCustomUserFields] = useState<any[]>([]);
  const [showAddField, setShowAddField] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldValue, setNewFieldValue] = useState('');
  const [userNotes, setUserNotes] = useState(data.userNotes || '');
  const [isLoadingUGC, setIsLoadingUGC] = useState(false);

  // Load user's custom fields and edits from backend
  useEffect(() => {
    const loadUGCFields = async () => {
      const leadId = lead.id || lead.personId;
      if (!leadId) return;
      
      setIsLoadingUGC(true);
      try {
        const response = await api.get(`/api/leads/${leadId}/ugc`);
        if (response.data?.success && response.data?.edits) {
          const edits = response.data.edits;
          
          // Separate custom fields from regular field edits
          const customFields: any[] = [];
          const fieldEdits: Record<string, any> = {};
          
          for (const edit of edits) {
            if (edit.is_custom || edit.field_name.startsWith('custom_')) {
              // It's a custom field
              const fieldName = edit.field_name.replace('custom_', '');
              customFields.push({
                id: edit.field_name,
                name: fieldName,
                value: edit.field_value,
                isUGC: true,
                addedAt: edit.created_at,
              });
            } else if (edit.field_name === 'user_notes') {
              // It's the notes field
              setUserNotes(edit.field_value || '');
            } else {
              // It's a regular field edit
              fieldEdits[edit.field_name] = {
                value: edit.field_value,
                editedAt: edit.updated_at || edit.created_at,
                isUGC: true,
              };
            }
          }
          
          if (customFields.length > 0) {
            setCustomUserFields(customFields);
          }
          if (Object.keys(fieldEdits).length > 0) {
            setUserEdits(fieldEdits);
          }
        }
      } catch (err) {
        console.error('Failed to load UGC fields:', err);
        // Silently fail - custom fields are optional
      } finally {
        setIsLoadingUGC(false);
      }
    };
    
    loadUGCFields();
  }, [lead.id, lead.personId]);

  // Check email connection status for smart Gmail sending
  useEffect(() => {
    const checkEmailConnection = async () => {
      try {
        const response = await api.get('/api/email/status');
        if (response.data) {
          setEmailConnection({
            connected: response.data.connected,
            provider: response.data.connection?.provider,
            email: response.data.connection?.email
          });
        }
      } catch (err) {
        // Not connected or error - will use browser compose
        setEmailConnection({ connected: false });
      }
    };
    
    checkEmailConnection();
  }, []);

  // Waterfall contact reveal: auto-trigger when opening a Precision Search result
  // that has no email/phone. Tries ZoomInfo first, falls back to ContactOut.
  useEffect(() => {
    const isPrecisionResult = data._source === 'contactout' || data.isPrecisionResult || isPrecisionMatch;
    const hasNoContactInfo = !email && !phone;
    const hasIdentity = fullName && fullName !== 'Unknown' && companyName;
    
    if (isPrecisionResult && hasNoContactInfo && hasIdentity && !enrichmentAttempted && !isEnrichingContact) {
      setIsEnrichingContact(true);
      setEnrichmentAttempted(true);
      
      const revealContact = async () => {
        try {
          const linkedInUrl = data.linkedInUrl || data.linkedinUrl || data.linkedin_url || '';
          console.log(`🔓 Revealing contact: ${fullName} @ ${companyName} (LinkedIn: ${linkedInUrl || 'none'})`);
          
          const response = await api.post('/api/person-enrich/reveal', {
            fullName,
            companyName,
            linkedInUrl: linkedInUrl || undefined,
          });
          
          if (response.data?.success) {
            console.log(`✅ Contact reveal: ${response.data.emails?.length || 0} emails, ${response.data.phones?.length || 0} phones (source: ${response.data.source})`);
            setEnrichedContactData(response.data);
          } else {
            console.log(`⚠️ Contact reveal: no data found for ${fullName} @ ${companyName}`);
            // Still store company data if available
            if (response.data?.companyData) {
              setEnrichedContactData(response.data);
            }
          }
        } catch (err: any) {
          console.warn('Contact reveal failed:', err?.message || err);
        } finally {
          setIsEnrichingContact(false);
        }
      };
      
      revealContact();
    }
  }, [fullName, companyName, email, phone, isPrecisionMatch, data._source, data.isPrecisionResult, enrichmentAttempted, isEnrichingContact]);
  
  // Reset enrichment state when lead changes
  useEffect(() => {
    setEnrichedContactData(null);
    setEnrichmentAttempted(false);
    setIsEnrichingContact(false);
  }, [lead.id, lead.personId, fullName]);

  // Derive effective contact info — prefer existing, then enriched data from reveal endpoint
  // The reveal endpoint returns: { emails: [{type, value, source}], phones: [{type, value, source}], companyData: {...} }
  const revealedEmails: Array<{type: string; value: string; source: string}> = enrichedContactData?.emails || [];
  const revealedPhones: Array<{type: string; value: string; source: string}> = enrichedContactData?.phones || [];
  
  const effectiveEmail = email || enrichedContactData?.email || (revealedEmails[0]?.value) || '';
  const effectivePhone = phone || enrichedContactData?.phone || (revealedPhones[0]?.value) || '';
  const effectiveMobilePhone = mobilePhone || enrichedContactData?.mobilePhone || 
    (revealedPhones.find((p: any) => p.type === 'mobile')?.value) || '';
  
  // Company data from reveal endpoint or direct fields
  const revealedCompany = enrichedContactData?.companyData;
  const effectiveCompanyIndustry = industry || revealedCompany?.industry || '';
  const effectiveCompanyEmployees = companyEmployees || revealedCompany?.employees || '';
  const effectiveCompanyRevenue = companyRevenue || revealedCompany?.revenue || '';
  const effectiveCompanyWebsite = companyWebsite || revealedCompany?.website || '';
  const effectiveProfilePicture = profilePicture || enrichedContactData?.profilePicture || null;
  
  // Did we actually get contact info from the reveal?
  const hasRevealedContactInfo = !!(effectiveEmail || effectivePhone);

  // Start editing a field
  const startEditing = (fieldName: string, currentValue: string) => {
    setEditingField(fieldName);
    setEditValue(currentValue || '');
  };

  // Save field edit - tags as UGC
  const saveFieldEdit = async (fieldName: string) => {
    const newEdits = {
      ...userEdits,
      [fieldName]: {
        value: editValue,
        editedAt: new Date().toISOString(),
        isUGC: true,
      }
    };
    setUserEdits(newEdits);
    setEditingField(null);
    
    // Persist to backend
    try {
      await api.post('/api/leads/update-field', {
        leadId: lead.id || lead.personId,
        fieldName,
        value: editValue,
        isUGC: true,
      });
      console.log(`📝 UGC field update: ${fieldName} = ${editValue}`);
    } catch (err) {
      console.error('Failed to save field edit:', err);
    }
  };

  // Add new custom field
  const addCustomField = async () => {
    if (!newFieldName.trim()) return;
    
    const newField = {
      id: `custom_${Date.now()}`,
      name: newFieldName.trim(),
      value: newFieldValue.trim(),
      isUGC: true,
      addedAt: new Date().toISOString(),
    };
    
    setCustomUserFields([...customUserFields, newField]);
    setNewFieldName('');
    setNewFieldValue('');
    setShowAddField(false);
    
    // Persist to backend
    try {
      await api.post('/api/leads/add-custom-field', {
        leadId: lead.id || lead.personId,
        fieldName: newField.name,
        fieldValue: newField.value,
        isUGC: true,
      });
    } catch (err) {
      console.error('Failed to add custom field:', err);
    }
  };

  // Delete a custom field
  const deleteCustomField = async (fieldId: string) => {
    setCustomUserFields(customUserFields.filter(f => f.id !== fieldId));
    
    try {
      await api.post('/api/leads/delete-custom-field', {
        leadId: lead.id || lead.personId,
        fieldId,
      });
    } catch (err) {
      console.error('Failed to delete field:', err);
    }
  };

  // Save user notes
  const saveNotes = async () => {
    try {
      await api.post('/api/leads/update-notes', {
        leadId: lead.id || lead.personId,
        notes: userNotes,
        isUGC: true,
      });
      console.log('📝 Notes saved');
    } catch (err) {
      console.error('Failed to save notes:', err);
    }
  };

  // Get display value (user edit or original)
  const getFieldValue = (fieldName: string, originalValue: string) => {
    if (userEdits[fieldName]) {
      return userEdits[fieldName].value;
    }
    return originalValue;
  };

  // Check if field was edited by user
  const isFieldEdited = (fieldName: string) => {
    return !!userEdits[fieldName];
  };

  // Find contacts at this company - ZIA: Must use real company-filtered data
  const handleFindContacts = async () => {
    if (!companyName && !fullName) return;
    
    setIsLoadingContacts(true);
    try {
      // Use infer-decision-maker which properly filters by company name via ZoomInfo
      // This ensures we only get REAL contacts at this specific company
      const response = await api.post('/api/ai/infer-decision-maker', {
        companyName: companyName || fullName,
        jobTitle: jobTitle || '',
        industry: industry || '',
      });
      
      if (response.data?.allContacts && response.data.allContacts.length > 0) {
        // Real contacts found at this company
        setFoundContacts(response.data.allContacts);
        setShowContactsSection(true);
      } else if (response.data?.decisionMaker && response.data.decisionMaker.name) {
        // Single decision maker found
        setFoundContacts([response.data.decisionMaker]);
        setShowContactsSection(true);
      } else {
        // ZIA: No fake data - show empty state with honest message
        setFoundContacts([]);
        setShowContactsSection(true);
      }
    } catch (err) {
      console.error('Failed to find contacts:', err);
      // ZIA: Don't fall back to general search that returns wrong data
      setFoundContacts([]);
      setShowContactsSection(true);
    } finally {
      setIsLoadingContacts(false);
    }
  };

  // Find/infer decision maker
  const handleFindDecisionMaker = async () => {
    setIsLoadingContacts(true);
    try {
      const response = await api.post('/api/ai/infer-decision-maker', {
        companyName: companyName || fullName,
        jobTitle: jobTitle,
        industry: industry,
      });
      
      if (response.data?.allContacts) {
        setFoundContacts(response.data.allContacts);
        setShowContactsSection(true);
      } else if (response.data?.decisionMaker) {
        setFoundContacts([response.data.decisionMaker]);
        setShowContactsSection(true);
      }
    } catch (err) {
      console.error('Failed to find decision maker:', err);
      // Fallback to regular contact search
      handleFindContacts();
    } finally {
      setIsLoadingContacts(false);
    }
  };
  
  // Fetch Deep Dive / Why Now insights
  const fetchWhyNow = async () => {
    if (!companyName && !fullName) return;
    
    setIsLoadingWhyNow(true);
    try {
      const res = await api.post('/api/ai/why-now', {
        contact: {
          name: fullName,
          title: jobTitle,
          company: companyName,
          linkedin: linkedinUrl,
        },
        companyContext: {
          industry: industry,
        },
      });
      
      if (res.data?.success) {
        setWhyNowData(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch Why Now:', err);
    } finally {
      setIsLoadingWhyNow(false);
    }
  };
  
  // Copy to clipboard
  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(field);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };


  // Check if this lead is liked (handle both 'liked' and 'shortlist' statuses)
  const isLiked = status === 'liked' || status === 'shortlist';
  const isArchived = status === 'archived';

  return (
    <aside 
      className="w-full h-full bg-white border-l border-black/5 flex flex-col overflow-hidden"
      style={{ 
        animation: 'eq-slide-from-right 300ms cubic-bezier(0.16, 1, 0.3, 1)',
        fontFamily: "'Playfair Display', serif",
      }}
    >
      {/* Premium animated styles */}
      <style>{`
        @keyframes eq-slide-from-right {
          from { transform: translateX(100%); opacity: 0.5; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes eq-section-reveal {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      
      {/* Header - Glass effect with gradient accent */}
      <header className={`border-b border-black/5 flex items-center justify-between gap-3 flex-shrink-0 bg-white/95 backdrop-blur-xl ${
        isMobile 
          ? 'px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))] sticky top-0 z-10' 
          : 'px-4 py-3'
      }`}>
        {/* Left: Back/Close button - Premium styling */}
        <button
          onClick={onClose}
          className="flex items-center gap-2 px-3 py-2 rounded-full bg-black/5 hover:bg-[#5a20ff]/10 active:scale-95 transition-all duration-200 flex-shrink-0"
        >
          <ArrowLeft className="w-5 h-5 text-[#5a20ff]" />
          <span className="text-sm font-semibold text-black">Back</span>
        </button>
        
        {/* Center: Title */}
        <h2 className="text-sm font-medium text-black/50 truncate flex-1 text-center">
          Lead Profile
        </h2>
        
        {/* Right: Quick action buttons - Premium X/✓ */}
        {onStatusChange && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Pass button - X */}
            <button
              onClick={() => {
                onStatusChange('archived');
                onClose();
              }}
              className={`w-11 h-11 rounded-full flex items-center justify-center transition-all duration-200 touch-manipulation ${
                isArchived
                  ? 'bg-slate-500 text-white shadow-lg shadow-slate-500/30'
                  : 'bg-white border-2 border-black/10 text-black/40 hover:border-red-400 hover:bg-red-50 hover:text-red-500 hover:scale-110 active:scale-95'
              }`}
              title="Pass on this lead"
            >
              <X className="w-5 h-5" />
            </button>
            
            {/* Like button - Check */}
            <button
              onClick={() => {
                onStatusChange('liked');
              }}
              className={`w-11 h-11 rounded-full flex items-center justify-center transition-all duration-200 touch-manipulation ${
                isLiked
                  ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-400/50'
                  : 'bg-white border-2 border-slate-200 text-slate-300 hover:border-emerald-400 hover:text-emerald-500 hover:bg-emerald-50 hover:scale-110 active:scale-90 active:bg-emerald-100'
              }`}
              title="Like this lead"
            >
              <Check className="w-5 h-5" />
            </button>
          </div>
        )}
      </header>

      {/* Content - with safe area padding for iOS home indicator */}
      <div className={`flex-1 overflow-y-auto ${isMobile ? 'pb-[env(safe-area-inset-bottom)]' : ''}`}>
        {/* Profile Header with Profile Picture */}
        <div className="p-5 border-b border-black/5">
          <div className="flex items-start gap-4">
            {/* Profile Picture / Company Logo */}
            <div className="relative flex-shrink-0">
              {/* Show profile picture for people */}
              {effectiveProfilePicture && !isCompanyResult ? (
                <img 
                  src={effectiveProfilePicture} 
                  alt={fullName}
                  className="w-20 h-20 rounded-full object-cover shadow-md"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                    const next = (e.target as HTMLImageElement).nextSibling as HTMLElement;
                    if (next) next.style.display = 'flex';
                  }}
                />
              ) : companyLogo ? (
                /* Show company logo for companies, jobs, or when we have a logo */
                <img 
                  src={companyLogo} 
                  alt={companyName || fullName}
                  className="w-20 h-20 rounded-xl object-contain bg-white border border-black/10 shadow-md p-1"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                    const next = (e.target as HTMLImageElement).nextSibling as HTMLElement;
                    if (next) next.style.display = 'flex';
                  }}
                />
              ) : null}
              {/* Fallback avatar */}
              <div 
                className={`w-20 h-20 ${isCompanyResult ? 'rounded-xl' : 'rounded-full'} bg-gradient-to-br from-[var(--eq-purple)] to-[var(--eq-teal)] flex items-center justify-center text-white text-3xl font-bold shadow-md`}
                style={{ display: (effectiveProfilePicture || companyLogo) ? 'none' : 'flex' }}
              >
                {isCompanyResult ? <Building2 className="w-10 h-10" /> : fullName.charAt(0).toUpperCase()}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              {/* Editable Name */}
              {editingField === 'fullName' ? (
                <div className="flex items-center gap-2 mb-1">
                  <input
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="text-xl font-semibold text-slate-900 bg-violet-50 px-2 py-1 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--eq-purple)]/30 flex-1 border border-violet-200"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                    autoFocus
                  />
                  <button onClick={() => saveFieldEdit('fullName')} className="p-1.5 bg-[var(--eq-purple)] rounded-lg">
                    <Check className="w-4 h-4 text-white" />
                  </button>
                  <button onClick={() => setEditingField(null)} className="p-1.5 bg-slate-100 rounded-lg">
                    <X className="w-4 h-4 text-slate-500" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 group">
                  <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {getFieldValue('fullName', fullName)}
                  </h3>
                  <button 
                    onClick={() => startEditing('fullName', getFieldValue('fullName', fullName))}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-slate-100 rounded transition-all"
                    title="Edit name"
                  >
                    <Pencil className="w-3 h-3 text-slate-400" />
                  </button>
                  {isFieldEdited('fullName') && (
                    <span className="text-[9px] px-1.5 py-0.5 bg-[var(--eq-teal)]/15 text-[var(--eq-teal-text)] rounded font-medium border border-[var(--eq-teal)]/30">UGC</span>
                  )}
                </div>
              )}
              
              {/* Editable Job Title */}
              {editingField === 'jobTitle' ? (
                <div className="flex items-center gap-2 mb-1">
                  <input
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="text-sm text-slate-600 bg-violet-50 px-2 py-1 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--eq-purple)]/30 flex-1 border border-violet-200"
                    autoFocus
                  />
                  <button onClick={() => saveFieldEdit('jobTitle')} className="p-1 bg-[var(--eq-purple)] rounded">
                    <Check className="w-3 h-3 text-white" />
                  </button>
                  <button onClick={() => setEditingField(null)} className="p-1 bg-slate-100 rounded">
                    <X className="w-3 h-3 text-slate-500" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 group">
                  <p className="text-sm text-slate-600">{getFieldValue('jobTitle', jobTitle)}</p>
                  <button 
                    onClick={() => startEditing('jobTitle', getFieldValue('jobTitle', jobTitle))}
                    className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-slate-100 rounded transition-all"
                    title="Edit title"
                  >
                    <Pencil className="w-2.5 h-2.5 text-slate-400" />
                  </button>
                  {isFieldEdited('jobTitle') && (
                    <span className="text-[9px] px-1 py-0.5 bg-[var(--eq-teal)]/15 text-[var(--eq-teal-text)] rounded font-medium border border-[var(--eq-teal)]/30">UGC</span>
                  )}
                </div>
              )}
              
              {companyName && (
                <div className="relative">
                  <button
                    onClick={() => setShowCompanyMenu(!showCompanyMenu)}
                    className="text-sm text-slate-500 flex items-center gap-1.5 mt-1 hover:text-[var(--eq-purple-text)] transition-colors group"
                  >
                    <Building2 className="w-4 h-4" />
                    <span>{companyName}</span>
                    <MousePointer className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                  
                  {/* Company action menu */}
                  {showCompanyMenu && (
                    <div className="absolute top-full left-0 mt-1 z-50 bg-white rounded-xl shadow-xl border border-slate-200 p-2 w-56">
                      <div className="text-xs text-slate-400 px-2 py-1 mb-1">Search for:</div>
                      <button
                        onClick={() => {
                          setShowCompanyMenu(false);
                          onSimilarSearch?.(companyName, 'people');
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-violet-50 rounded-lg transition-colors"
                      >
                        <Users className="w-4 h-4 text-[var(--eq-purple-text)]" />
                        <span>People at {companyName}</span>
                      </button>
                      <button
                        onClick={() => {
                          setShowCompanyMenu(false);
                          onSimilarSearch?.(companyName, 'company');
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-emerald-50 rounded-lg transition-colors"
                      >
                        <Building2 className="w-4 h-4 text-[var(--eq-teal-text)]" />
                        <span>Similar companies</span>
                      </button>
                      <button
                        onClick={() => {
                          setShowCompanyMenu(false);
                          onSimilarSearch?.(companyName, 'jobs');
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-orange-50 rounded-lg transition-colors"
                      >
                        <Briefcase className="w-4 h-4 text-orange-600" />
                        <span>Open jobs</span>
                      </button>
                      <div className="border-t border-slate-100 mt-1 pt-1">
                        <button
                          onClick={() => setShowCompanyMenu(false)}
                          className="w-full px-3 py-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* Professional Pills - Show for people results */}
          {!isCompanyResult && !isJobResult && !isNewsResult && (managementLevel || experienceYears || (education && education.length > 0) || (skills && skills.length > 0) || isPrecisionMatch || isOpenToWork) && (
            <div className="flex flex-wrap gap-2 mt-4">
              {/* Open to Work - High priority indicator */}
              {isOpenToWork && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-200 animate-pulse">
                  <Target className="w-3 h-3" />
                  Open to Work
                </span>
              )}
              {/* Precision Match indicator */}
              {isPrecisionMatch && (
                <span 
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200"
                  title={matchedKeywords.length > 0 ? `Matched: ${matchedKeywords.join(', ')}` : 'Matched via keyword search'}
                >
                  <Sparkles className="w-3 h-3" />
                  Precision Match
                  {matchedKeywords.length > 0 && (
                    <span className="text-amber-500">({matchedKeywords.join(', ')})</span>
                  )}
                </span>
              )}
              {managementLevel && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-violet-100 text-[var(--eq-purple-text)] border border-violet-200">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" /></svg>
                  {Array.isArray(managementLevel) ? managementLevel[0] : managementLevel}
                </span>
              )}
              {experienceYears && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                  <Briefcase className="w-3 h-3" />
                  {experienceYears} years exp
                </span>
              )}
              {education && education.length > 0 && education[0]?.school && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" /></svg>
                  {education[0].school}
                </span>
              )}
              {skills && skills.length > 0 && (
                <>
                  {skills.slice(0, 3).map((skill: string, idx: number) => (
                    <span key={idx} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200">
                      {skill}
                    </span>
                  ))}
                  {skills.length > 3 && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs text-slate-400">
                      +{skills.length - 3} more
                    </span>
                  )}
                </>
              )}
            </div>
          )}
          
          {/* Data Quality Indicator - Shows lead completeness at a glance */}
          <div className="mt-4 pt-3 border-t border-black/5">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-3.5 h-3.5 text-[var(--eq-purple)]" />
              <span className="text-[10px] font-semibold text-black/40 uppercase tracking-wide">Data Quality</span>
            </div>
            <DataQualityIndicator 
              hasEmail={!!effectiveEmail}
              hasPhone={!!effectivePhone || !!effectiveMobilePhone}
              hasLinkedIn={!!linkedInUrl}
              hasCompany={!!companyName}
              hasLocation={!!location}
            />
          </div>
        </div>

        {/* Company/Job/News: Find Contacts Section */}
        {(isCompanyResult || isJobResult || isNewsResult) && (
          <div className="p-5 border-b border-black/5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-black/40 uppercase tracking-wide">Find Contacts</p>
              {foundContacts.length > 0 && (
                <button
                  onClick={() => setShowContactsSection(!showContactsSection)}
                  className="text-xs text-[var(--eq-purple-text)]"
                >
                  {showContactsSection ? 'Hide' : 'Show'} ({foundContacts.length})
                </button>
              )}
            </div>
            
            {/* Action buttons */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={handleFindContacts}
                disabled={isLoadingContacts}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[var(--eq-purple)] text-white rounded-xl text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {isLoadingContacts ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Users className="w-4 h-4" />
                )}
                Find Contacts
              </button>
              <button
                onClick={handleFindDecisionMaker}
                disabled={isLoadingContacts}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[var(--eq-teal)]/10 text-[var(--eq-teal-text)] rounded-xl text-sm font-medium hover:bg-[var(--eq-teal)]/20 transition-colors disabled:opacity-50"
              >
                {isLoadingContacts ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Target className="w-4 h-4" />
                )}
                Decision Maker
              </button>
            </div>

            {/* Found contacts list */}
            {showContactsSection && foundContacts.length > 0 && (
              <div className="space-y-2">
                {foundContacts.map((contact, idx) => {
                  const contactData = contact.data || contact;
                  const contactName = contactData.name || contactData.fullName || 
                    `${contactData.firstName || ''} ${contactData.lastName || ''}`.trim() || 'Unknown';
                  const contactTitle = contactData.jobTitle || contactData.title || '';
                  const contactEmail = contactData.email || '';
                  const contactPhone = contactData.phone || '';
                  
                  return (
                    <div key={idx} className="p-3 bg-gradient-to-r from-[var(--eq-purple)]/5 to-[var(--eq-teal)]/5 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--eq-purple)] to-[var(--eq-teal)] flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                          {contactName.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-black truncate">{contactName}</p>
                          <p className="text-xs text-black/50 truncate">{contactTitle}</p>
                        </div>
                      </div>
                      
                      {/* Contact actions */}
                      <div className="flex gap-2 mt-3">
                        {contactEmail && (
                          <a
                            href={`mailto:${contactEmail}`}
                            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-white rounded-lg text-xs text-[var(--eq-purple-text)] hover:bg-[var(--eq-purple)]/10 transition-colors"
                          >
                            <Mail className="w-3.5 h-3.5" />
                            Email
                          </a>
                        )}
                        {contactPhone && (
                          <a
                            href={`tel:${contactPhone}`}
                            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-white rounded-lg text-xs text-[var(--eq-teal-text)] hover:bg-[var(--eq-teal)]/10 transition-colors"
                          >
                            <Phone className="w-3.5 h-3.5" />
                            Call
                          </a>
                        )}
                        <button
                          onClick={() => navigator.clipboard.writeText(contactEmail || contactPhone)}
                          className="px-3 py-2 bg-white rounded-lg text-xs text-black/40 hover:bg-black/5 transition-colors"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* No contacts found message - ZIA: Honest messaging, no fake data */}
            {showContactsSection && foundContacts.length === 0 && !isLoadingContacts && (
              <div className="text-center py-4 space-y-2">
                <p className="text-xs text-black/50">
                  No verified contacts found at {companyName || fullName}
                </p>
                <p className="text-[10px] text-black/30">
                  Try searching for specific roles via the main search
                </p>
              </div>
            )}
          </div>
        )}

        {/* Contact Info - For People results - EDITABLE */}
        {/* Uses effectiveEmail/effectivePhone which merge original data with ZoomInfo enrichment */}
        {!isCompanyResult && !isJobResult && !isNewsResult && (
        <div className="p-5 border-b border-black/5 space-y-3">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-black/40 uppercase tracking-wide">Contact Information</p>
            {isEnrichingContact && (
              <div className="flex items-center gap-1.5 text-emerald-600">
                <Loader2 className="w-3 h-3 animate-spin" />
                <span className="text-[10px] font-medium">Fetching contacts...</span>
              </div>
            )}
            {hasRevealedContactInfo && !isEnrichingContact && enrichedContactData?.cached && (
              <span className="text-[10px] text-[#5a20ff] bg-[#5a20ff]/10 px-1.5 py-0.5 rounded-full font-medium flex items-center gap-1">
                <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
                Previously revealed
              </span>
            )}
            {hasRevealedContactInfo && !isEnrichingContact && !enrichedContactData?.cached && (
              <span className="text-[10px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full font-medium">
                {enrichedContactData?.source === 'zoominfo' ? 'Verified' : enrichedContactData?.source === 'contactout' ? 'Found' : 'Verified'}
              </span>
            )}
          </div>
          
          {/* Editable Email */}
          {(effectiveEmail || editingField === 'email') && (
            <div className="flex items-center justify-between group">
              {editingField === 'email' ? (
                <div className="flex items-center gap-2 flex-1">
                  <Mail className="w-4 h-4 text-[var(--eq-purple-text)] flex-shrink-0" />
                  <input
                    type="email"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="flex-1 text-sm bg-[var(--eq-purple)]/5 px-2 py-1 rounded-lg focus:outline-none"
                    autoFocus
                  />
                  <button onClick={() => saveFieldEdit('email')} className="p-1 bg-[var(--eq-purple)] rounded">
                    <Check className="w-3 h-3 text-white" />
                  </button>
                  <button onClick={() => setEditingField(null)} className="p-1 bg-black/5 rounded">
                    <X className="w-3 h-3 text-black/40" />
                  </button>
                </div>
              ) : (
                <>
                  <a href={`mailto:${getFieldValue('email', effectiveEmail)}`} className="flex items-center gap-3 text-sm text-black/70 hover:text-[var(--eq-purple-text)] min-w-0 flex-1">
                    <Mail className="w-4 h-4 text-[var(--eq-purple-text)] flex-shrink-0" />
                    <span className="truncate">{getFieldValue('email', effectiveEmail)}</span>
                    {isFieldEdited('email') && (
                      <span className="text-[9px] px-1 py-0.5 bg-[var(--eq-teal)]/10 text-[var(--eq-teal-text)] rounded font-medium">UGC</span>
                    )}
                  </a>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                    <button
                      onClick={() => startEditing('email', getFieldValue('email', effectiveEmail))}
                      className="p-1.5 hover:bg-black/5 rounded-lg"
                      title="Edit email"
                    >
                      <Pencil className="w-3 h-3 text-black/30" />
                    </button>
                    <button
                      onClick={() => copyToClipboard(getFieldValue('email', effectiveEmail), 'email')}
                      className="p-1.5 hover:bg-black/5 rounded-lg"
                    >
                      {copied === 'email' ? <CheckCircle className="w-4 h-4 text-[var(--eq-teal-text)]" /> : <Copy className="w-4 h-4 text-black/40" />}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
          
          {/* Editable Phone */}
          {(effectivePhone || editingField === 'phone') && (
            <div className="flex items-center justify-between group">
              {editingField === 'phone' ? (
                <div className="flex items-center gap-2 flex-1">
                  <Phone className="w-4 h-4 text-[var(--eq-purple-text)] flex-shrink-0" />
                  <input
                    type="tel"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="flex-1 text-sm bg-[var(--eq-purple)]/5 px-2 py-1 rounded-lg focus:outline-none"
                    autoFocus
                  />
                  <button onClick={() => saveFieldEdit('phone')} className="p-1 bg-[var(--eq-purple)] rounded">
                    <Check className="w-3 h-3 text-white" />
                  </button>
                  <button onClick={() => setEditingField(null)} className="p-1 bg-black/5 rounded">
                    <X className="w-3 h-3 text-black/40" />
                  </button>
                </div>
              ) : (
                <>
                  <a href={`tel:${getFieldValue('phone', effectivePhone)}`} className="flex items-center gap-3 text-sm text-black/70 hover:text-[var(--eq-purple-text)]">
                    <Phone className="w-4 h-4 text-[var(--eq-purple-text)] flex-shrink-0" />
                    <span>{getFieldValue('phone', effectivePhone)}</span>
                    {isFieldEdited('phone') && (
                      <span className="text-[9px] px-1 py-0.5 bg-[var(--eq-teal)]/10 text-[var(--eq-teal-text)] rounded font-medium">UGC</span>
                    )}
                  </a>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                    <button
                      onClick={() => startEditing('phone', getFieldValue('phone', effectivePhone))}
                      className="p-1.5 hover:bg-black/5 rounded-lg"
                      title="Edit phone"
                    >
                      <Pencil className="w-3 h-3 text-black/30" />
                    </button>
                    <button
                      onClick={() => copyToClipboard(getFieldValue('phone', effectivePhone), 'phone')}
                      className="p-1.5 hover:bg-black/5 rounded-lg"
                    >
                      {copied === 'phone' ? <CheckCircle className="w-4 h-4 text-[var(--eq-teal-text)]" /> : <Copy className="w-4 h-4 text-black/40" />}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {mobilePhone && mobilePhone !== phone && (
            <div className="flex items-center justify-between group">
              <a href={`tel:${mobilePhone}`} className="flex items-center gap-3 text-sm text-black/70 hover:text-[var(--eq-purple-text)]">
                <Phone className="w-4 h-4 text-[var(--eq-teal-text)] flex-shrink-0" />
                <span>{mobilePhone} (mobile)</span>
              </a>
              <button
                onClick={() => copyToClipboard(mobilePhone, 'mobile')}
                className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-black/5 rounded-lg transition-all"
              >
                {copied === 'mobile' ? <CheckCircle className="w-4 h-4 text-[var(--eq-teal-text)]" /> : <Copy className="w-4 h-4 text-black/40" />}
              </button>
            </div>
          )}
          
          {linkedinUrl && (
            <a 
              href={linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 text-sm text-[var(--eq-purple-text)] hover:underline"
            >
              <Linkedin className="w-4 h-4" />
              InMail
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
          
          {/* Supplemental emails — show additional emails from reveal (after the primary) */}
          {revealedEmails.length > 1 && (
            <div className="mt-1 space-y-1.5">
              {revealedEmails.slice(1).map((em: any, idx: number) => (
                <div key={idx} className="flex items-center justify-between group">
                  <a href={`mailto:${em.value}`} className="flex items-center gap-3 text-sm text-black/60 hover:text-[var(--eq-purple-text)] min-w-0 flex-1">
                    <Mail className="w-3.5 h-3.5 text-black/30 flex-shrink-0" />
                    <span className="truncate text-xs">{em.value}</span>
                    <span className="text-[9px] px-1 py-0.5 bg-slate-100 text-slate-500 rounded font-medium flex-shrink-0">
                      {em.type}
                    </span>
                  </a>
                  <button
                    onClick={() => copyToClipboard(em.value, `email-${idx}`)}
                    className="p-1 opacity-0 group-hover:opacity-100 hover:bg-black/5 rounded-lg transition-all"
                  >
                    {copied === `email-${idx}` ? <CheckCircle className="w-3.5 h-3.5 text-[var(--eq-teal-text)]" /> : <Copy className="w-3.5 h-3.5 text-black/30" />}
                  </button>
                </div>
              ))}
            </div>
          )}
          
          {/* Supplemental phones — show additional phones from reveal (after the primary) */}
          {revealedPhones.length > 1 && (
            <div className="mt-1 space-y-1.5">
              {revealedPhones.slice(1).map((ph: any, idx: number) => (
                <div key={idx} className="flex items-center justify-between group">
                  <a href={`tel:${ph.value}`} className="flex items-center gap-3 text-sm text-black/60 hover:text-[var(--eq-purple-text)]">
                    <Phone className="w-3.5 h-3.5 text-black/30 flex-shrink-0" />
                    <span className="text-xs">{ph.value}</span>
                    <span className="text-[9px] px-1 py-0.5 bg-slate-100 text-slate-500 rounded font-medium flex-shrink-0">
                      {ph.type}
                    </span>
                  </a>
                  <button
                    onClick={() => copyToClipboard(ph.value, `phone-${idx}`)}
                    className="p-1 opacity-0 group-hover:opacity-100 hover:bg-black/5 rounded-lg transition-all"
                  >
                    {copied === `phone-${idx}` ? <CheckCircle className="w-3.5 h-3.5 text-[var(--eq-teal-text)]" /> : <Copy className="w-3.5 h-3.5 text-black/30" />}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add missing field button — only show when not enriching and no contact info found */}
          {!effectiveEmail && !effectivePhone && !isEnrichingContact && (
            <div className="text-center py-2">
              <button
                onClick={() => {
                  if (!effectiveEmail) startEditing('email', '');
                  else if (!effectivePhone) startEditing('phone', '');
                }}
                className="text-xs text-[var(--eq-purple-text)] hover:underline"
              >
                + Add contact info
              </button>
            </div>
          )}
        </div>
        )}
        
        {/* User Custom Fields - EDITABLE */}
        {customUserFields.length > 0 && (
          <div className="p-5 border-b border-black/5 space-y-3">
            <p className="text-xs font-semibold text-black/40 uppercase tracking-wide mb-3">Custom Fields</p>
            {customUserFields.map((field) => (
              <div key={field.id} className="flex items-center justify-between group">
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-black/40">{field.name}:</span>
                  <span className="text-black/70">{field.value}</span>
                  <span className="text-[9px] px-1 py-0.5 bg-[var(--eq-teal)]/10 text-[var(--eq-teal-text)] rounded font-medium">UGC</span>
                </div>
                <button
                  onClick={() => deleteCustomField(field.id)}
                  className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-red-50 rounded-lg transition-all"
                  title="Delete field"
                >
                  <MinusCircle className="w-3.5 h-3.5 text-red-400" />
                </button>
              </div>
            ))}
          </div>
        )}
        
        {/* Deep Dive / Why Now - DEX: Key insight for outreach timing */}
        {!isCompanyResult && !isJobResult && !isNewsResult && companyName && (
          <div className="p-5 border-b border-black/5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-black/40 uppercase tracking-wide flex items-center gap-2">
                <Lightbulb className="w-3.5 h-3.5" />
                Deep Dive
              </p>
              {whyNowData && (
                <button
                  onClick={() => {
                    setWhyNowData(null);
                    fetchWhyNow();
                  }}
                  className="text-xs text-[var(--eq-purple-text)] hover:underline flex items-center gap-1"
                >
                  <RefreshCw className="w-3 h-3" />
                  Refresh
                </button>
              )}
            </div>
            
            {!whyNowData && !isLoadingWhyNow && (
              <button
                onClick={fetchWhyNow}
                className="w-full py-3 bg-gradient-to-r from-[var(--eq-purple)]/10 to-[var(--eq-teal)]/10 
                           text-[var(--eq-purple-text)] rounded-xl text-sm font-medium 
                           hover:from-[var(--eq-purple)]/20 hover:to-[var(--eq-teal)]/20 
                           transition-all duration-300 flex items-center justify-center gap-2"
              >
                <Zap className="w-4 h-4" />
                Research Why Now
              </button>
            )}
            
            {isLoadingWhyNow && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-5 h-5 text-[var(--eq-purple)] animate-spin" />
                <span className="ml-2 text-sm text-black/50">Researching...</span>
              </div>
            )}
            
            {whyNowData && (
              <div className="space-y-3">
                {/* Confidence Badge */}
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                    whyNowData.confidence === 'high' ? 'bg-emerald-100 text-emerald-700' :
                    whyNowData.confidence === 'medium' ? 'bg-amber-100 text-amber-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>
                    {whyNowData.confidence === 'high' ? 'High Signal' : 
                     whyNowData.confidence === 'medium' ? 'Medium' : 'Low Signal'}
                  </span>
                </div>
                
                {/* Why Now reasoning */}
                <div className="p-3 bg-[var(--eq-purple)]/5 rounded-xl">
                  <p className="text-sm text-black/70 leading-relaxed">
                    {whyNowData.whyNow}
                  </p>
                </div>
                
                {/* Suggested Opener */}
                {whyNowData.suggestedOpener && (
                  <div className="p-3 bg-gradient-to-br from-[var(--eq-purple)]/5 to-[var(--eq-teal)]/5 rounded-xl border border-black/5">
                    <p className="text-[10px] font-semibold text-black/40 uppercase tracking-wide mb-1">
                      Suggested Opener
                    </p>
                    <p 
                      className="text-sm text-black italic leading-relaxed"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      "{whyNowData.suggestedOpener}"
                    </p>
                    <button
                      onClick={() => copyToClipboard(whyNowData.suggestedOpener, 'opener')}
                      className="mt-2 text-xs text-[var(--eq-purple-text)] hover:underline flex items-center gap-1"
                    >
                      {copied === 'opener' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      {copied === 'opener' ? 'Copied' : 'Copy opener'}
                    </button>
                  </div>
                )}
                
                {/* Open positions indicator - CLICKABLE: Opens jobs in new tab */}
                {whyNowData.openJobs > 0 && (
                  <button
                    onClick={() => {
                      // Open jobs search in new tab so user can return to this contact
                      const jobsSearchUrl = `/search?mode=jobs&q=${encodeURIComponent(companyName + ' jobs')}`;
                      window.open(jobsSearchUrl, '_blank');
                    }}
                    className="flex items-center gap-2 text-xs text-emerald-600 hover:text-emerald-700 hover:underline cursor-pointer group transition-colors"
                  >
                    <Briefcase className="w-3.5 h-3.5" />
                    <span>{whyNowData.openJobs} open positions at {companyName}</span>
                    <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* About/Summary - From ContactOut rich data */}
        {!isCompanyResult && !isJobResult && !isNewsResult && summary && (
          <CollapsibleSection 
            title="About" 
            priority="primary"
            defaultOpen={true}
          >
            <p className="text-sm text-black/70 leading-relaxed whitespace-pre-line">
              {summary}
            </p>
          </CollapsibleSection>
        )}

        {/* Skills - Full list from ContactOut */}
        {!isCompanyResult && !isJobResult && !isNewsResult && skills && skills.length > 0 && (
          <CollapsibleSection 
            title="Skills" 
            badge={skills.length}
            priority="primary"
            defaultOpen={true}
          >
            <div className="flex flex-wrap gap-1.5">
              {skills.map((skill: string, idx: number) => (
                <span 
                  key={idx} 
                  className="px-2 py-1 bg-slate-100 text-slate-700 rounded-lg text-xs font-medium hover:bg-[var(--eq-purple)]/10 hover:text-[var(--eq-purple-text)] transition-colors cursor-pointer"
                  title={`Search for people with ${skill}`}
                >
                  {skill}
                </span>
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Work Experience - ENHANCED with summaries from ContactOut */}
        {/* DEX: Critical for recruiters - defaultOpen=true */}
        {!isCompanyResult && !isJobResult && !isNewsResult && employmentHistory && employmentHistory.length > 0 && (
          <CollapsibleSection 
            title="Work Experience" 
            badge={employmentHistory.length}
            priority="primary"
            defaultOpen={true}
          >
            <div className="space-y-4">
              {employmentHistory.slice(0, 15).map((job: any, idx: number) => {
                // Safely extract company name - handle case where job.company is an object
                const jobCompanyName = typeof job.company === 'object' 
                  ? (job.company?.companyName || job.company?.name || job.companyName || 'Unknown Company')
                  : (job.company || job.companyName || 'Unknown Company');
                
                // Get job summary/description - this is the rich data from ContactOut
                const jobSummary = job.summary || job.description || job.responsibilities || '';
                const jobLocation = job.location || job.locality || '';
                const isCurrent = job.isCurrent || job.is_current || (!job.endDate && !job.end_date && !job.toDate);
                
                return (
                  <div key={idx} className="relative pl-4 border-l-2 border-slate-200 hover:border-[var(--eq-purple)] transition-colors">
                    {/* Current role indicator */}
                    {isCurrent && idx === 0 && (
                      <span className="absolute -left-[5px] top-0 w-2 h-2 rounded-full bg-green-500" title="Current position" />
                    )}
                    
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-black">
                        {job.title || job.jobTitle || 'Unknown Role'}
                      </p>
                      <p className="text-xs font-medium text-[var(--eq-purple-text)]">{jobCompanyName}</p>
                      
                      <div className="flex items-center gap-2 text-xs text-black/40">
                        {(job.startDate || job.start_date || job.fromDate) && (
                          <span>
                            {formatDate(job.startDate || job.start_date || job.fromDate)}
                            {' - '}
                            {job.endDate || job.end_date || job.toDate 
                              ? formatDate(job.endDate || job.end_date || job.toDate) 
                              : 'Present'}
                          </span>
                        )}
                        {jobLocation && (
                          <>
                            <span className="text-black/20">•</span>
                            <span>{jobLocation}</span>
                          </>
                        )}
                      </div>
                      
                      {/* Job summary/achievements - THE KEY RICH DATA */}
                      {jobSummary && (
                        <p className="text-xs text-black/60 mt-2 leading-relaxed whitespace-pre-line">
                          {jobSummary}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
              {employmentHistory.length > 15 && (
                <p className="text-xs text-black/40 italic pl-4">+{employmentHistory.length - 15} more positions</p>
              )}
            </div>
          </CollapsibleSection>
        )}
        
        {/* Education - Enhanced display */}
        {!isCompanyResult && !isJobResult && !isNewsResult && education && education.length > 0 && (
          <CollapsibleSection 
            title="Education" 
            badge={education.length}
            priority="secondary"
            defaultOpen={false}
          >
            <div className="space-y-3">
              {education.map((edu: any, idx: number) => (
                <div key={idx} className="pl-4 border-l-2 border-slate-200">
                  <p className="text-sm font-medium text-black">
                    {edu.school || edu.institution || edu.university || 'Unknown School'}
                  </p>
                  {(edu.degree || edu.field_of_study || edu.fieldOfStudy) && (
                    <p className="text-xs text-black/60">
                      {edu.degree}{edu.field_of_study || edu.fieldOfStudy ? `, ${edu.field_of_study || edu.fieldOfStudy}` : ''}
                    </p>
                  )}
                  {(edu.start_date_year || edu.startYear || edu.end_date_year || edu.endYear) && (
                    <p className="text-xs text-black/40 mt-0.5">
                      {edu.start_date_year || edu.startYear || ''} - {edu.end_date_year || edu.endYear || 'Present'}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Details - Collapsible secondary section */}
        <CollapsibleSection 
          title="Details" 
          priority="secondary"
          defaultOpen={true}
        >
          <div className="space-y-3">
            {location && (
              <div className="flex items-center gap-3 text-sm text-black/60">
                <MapPin className="w-4 h-4 text-black/40" />
                <span>{location}</span>
              </div>
            )}

            {effectiveCompanyIndustry && (
              <div className="flex items-center gap-3 text-sm text-black/60">
                <Briefcase className="w-4 h-4 text-black/40" />
                <span>{effectiveCompanyIndustry}</span>
              </div>
            )}

            {effectiveCompanyEmployees && (
              <div className="flex items-center gap-3 text-sm text-black/60">
                <Users className="w-4 h-4 text-black/40" />
                <span>{typeof effectiveCompanyEmployees === 'number' ? effectiveCompanyEmployees.toLocaleString() : effectiveCompanyEmployees} employees</span>
              </div>
            )}

            {effectiveCompanyRevenue && (
              <div className="flex items-center gap-3 text-sm text-black/60">
                <TrendingUp className="w-4 h-4 text-black/40" />
                <span>{effectiveCompanyRevenue}</span>
              </div>
            )}

            {/* Company-specific fields */}
            {yearFounded && (
              <div className="flex items-center gap-3 text-sm text-black/60">
                <Building2 className="w-4 h-4 text-black/40" />
                <span>Founded {yearFounded}</span>
              </div>
            )}

            {effectiveCompanyWebsite && (
              <div className="flex items-center gap-3 text-sm text-black/60">
                <ExternalLink className="w-4 h-4 text-black/40" />
                <a href={effectiveCompanyWebsite.startsWith('http') ? effectiveCompanyWebsite : `https://${effectiveCompanyWebsite}`} target="_blank" rel="noopener noreferrer" className="text-[var(--eq-purple-text)] hover:underline">
                  {effectiveCompanyWebsite.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                </a>
              </div>
            )}

            {ticker && (
              <div className="flex items-center gap-3 text-sm text-black/60">
                <TrendingUp className="w-4 h-4 text-black/40" />
                <span>Stock: {ticker}</span>
              </div>
            )}

            {fundingAmount && (
              <div className="flex items-center gap-3 text-sm text-black/60">
                <Target className="w-4 h-4 text-black/40" />
                <span>Funding: ${typeof fundingAmount === 'number' ? fundingAmount.toLocaleString() : fundingAmount}</span>
              </div>
            )}

            {latestFundingRound && (
              <div className="flex items-center gap-3 text-sm text-black/60">
                <Target className="w-4 h-4 text-black/40" />
                <span>Latest Round: {latestFundingRound}</span>
              </div>
            )}
          </div>
        </CollapsibleSection>

        {/* Summary/Bio */}
        {summary && (
          <div className="p-5 border-b border-black/5">
            <p className="text-xs font-semibold text-black/40 uppercase tracking-wide mb-3">Summary</p>
            <p className="text-sm text-black/60 leading-relaxed">
              {summary}
            </p>
          </div>
        )}

        {/* Reach — single CTA to add to the Reach pipeline */}
        <div className="p-5">
          <p className="text-xs font-semibold text-black/40 uppercase tracking-wide mb-4">Reach</p>

          <button
            onClick={async () => {
              try {
                const idForReach = lead?.id || lead?.personId;
                await api.post(`/api/leads/${idForReach}/add-to-reach`);
                toast.success(`${firstName || fullName} added to Reach`);
              } catch (err) {
                console.error('Add to reach failed', err);
                toast.error('Failed to add to Reach');
              }
            }}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-[var(--eq-purple)] to-[var(--eq-teal)] text-white rounded-xl text-sm font-semibold hover:opacity-90 transition-all shadow-sm"
          >
            <Send className="w-4 h-4" />
            Add to Reach
          </button>
          <p className="text-xs text-black/40 text-center mt-2">Draft & send a personalised email from Reach</p>

          {/* Call option */}
          {phone && (
            <a
              href={`tel:${phone}`}
              className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-3 bg-[var(--eq-teal)]/10 text-[var(--eq-teal-text)] rounded-xl text-sm font-semibold hover:bg-[var(--eq-teal)]/20 transition-colors"
            >
              <Phone className="w-4 h-4" />
              Call {firstName || 'Lead'}
            </a>
          )}
        </div>

        {/* User Notes - At bottom for user additions */}
        <div className="p-5 border-t border-black/5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-black/40 uppercase tracking-wide">Notes</p>
            {userNotes && (
              <span className="text-[9px] px-1.5 py-0.5 bg-[var(--eq-teal)]/10 text-[var(--eq-teal-text)] rounded font-medium">Saved</span>
            )}
          </div>
          <textarea
            value={userNotes}
            onChange={(e) => setUserNotes(e.target.value)}
            onBlur={saveNotes}
            placeholder="Add notes about this lead..."
            className="w-full px-3 py-2 text-sm bg-white rounded-xl border border-black/10 focus:outline-none focus:ring-2 focus:ring-[#5a20ff]/20 resize-none"
            rows={3}
          />
        </div>

        {/* Add Custom Field - At the bottom after all existing data */}
        <div className="p-5 border-t border-black/5">
          {showAddField ? (
            <div className="bg-[var(--eq-purple)]/5 rounded-xl p-4">
              <p className="text-xs font-semibold text-black/40 uppercase tracking-wide mb-3">Add Custom Field</p>
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Field name (e.g., Notes, Relationship)"
                  value={newFieldName}
                  onChange={(e) => setNewFieldName(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-white rounded-lg border border-black/10 focus:outline-none focus:ring-2 focus:ring-[#5a20ff]/30"
                />
                <input
                  type="text"
                  placeholder="Value"
                  value={newFieldValue}
                  onChange={(e) => setNewFieldValue(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-white rounded-lg border border-black/10 focus:outline-none focus:ring-2 focus:ring-[#5a20ff]/30"
                />
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={addCustomField}
                    disabled={!newFieldName.trim()}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-[var(--eq-purple)] text-white rounded-lg text-sm font-medium disabled:opacity-50"
                  >
                    <Check className="w-4 h-4" />
                    Add Field
                  </button>
                  <button
                    onClick={() => { setShowAddField(false); setNewFieldName(''); setNewFieldValue(''); }}
                    className="px-3 py-2 bg-black/5 rounded-lg text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowAddField(true)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-sm text-[var(--eq-purple-text)] border-2 border-dashed border-black/10 hover:border-[var(--eq-purple)]/30 hover:bg-[var(--eq-purple)]/5 rounded-xl transition-colors"
            >
              <PlusSquare className="w-4 h-4" />
              Add Custom Field
            </button>
          )}
        </div>
      </div>

      {/* Footer - Quick Actions */}
      <footer className="p-4 border-t border-black/5 bg-[#f8f8f6]">
        <div className="flex items-center justify-between text-xs text-black/40">
          <span>Powered by EQ.app</span>
          <div className="flex items-center gap-2">
            {email && (
              <button
                onClick={() => copyToClipboard(email, 'email-footer')}
                className="p-1.5 hover:bg-black/5 rounded-lg transition-colors"
                title="Copy email"
              >
                {copied === 'email-footer' ? <CheckCircle className="w-4 h-4 text-[var(--eq-teal-text)]" /> : <Copy className="w-4 h-4" />}
              </button>
            )}
            {linkedinUrl && (
              <a
                href={linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 hover:bg-black/5 rounded-lg transition-colors"
                title="Open LinkedIn"
              >
                <Linkedin className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>
      </footer>
    </aside>
  );
}

export default LeadDetailPanel;
